<script>
  import Button from '../lib/components/Button.svelte';
  import Card from '../lib/components/Card.svelte';
  import { onMount } from 'svelte';
  import { Route, Clock, CheckCircle, FileDown, ArrowRight, Lock } from 'lucide-svelte';

  export let onStart;

  const features = [
    {
      icon: Route,
      title: 'Smart route optimisation',
      description: 'Automatically find the shortest route through all your delivery stops'
    },
    {
      icon: Clock,
      title: 'Real-time traffic',
      description: 'Consider current traffic conditions for accurate time estimates'
    },
    {
      icon: CheckCircle,
      title: 'Track your progress',
      description: 'Mark deliveries complete and see your progress in real time'
    },
    {
      icon: FileDown,
      title: 'Export records',
      description: 'Download your route data as CSV for timesheets and reports'
    }
  ];

  onMount(() => {
    // Component mounted
  });
</script>

<div class="min-h-screen relative overflow-hidden" style="background: var(--bg);">
  <!-- Mearsit koru watermark (optimized) -->
  <img
    src="/mearsit-koru-bg.png"
    alt=""
    class="absolute top-[5%] right-[-8%] w-[700px] h-[700px] opacity-[0.2] pointer-events-none"
    aria-hidden="true"
    loading="lazy"
    style="z-index: 1;"
  />

  <!-- Header with mearsit logo -->
  <header class="sticky top-0 z-50 backdrop-blur-md" style="background: rgba(250, 248, 243, 0.88); border-bottom: 1px solid var(--border);">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-20">
        <div class="flex items-center gap-4">
          <img src="/mearsit-lockup.png" alt="mearsit — software · analysis · delivery" class="h-12" style="display: block;" />
        </div>
        <p style="font-size: 1.125rem; font-weight: 600; color: var(--fg); font-family: var(--font-display);">ScriptRunner</p>
      </div>
    </div>
  </header>

  <div class="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
    <!-- Hero Section -->
    <div class="text-center mb-16">
      <div class="inline-block mb-6">
        <div class="flex items-center gap-2 px-4 py-2 rounded-full" style="background: var(--bg-raised); border: 1px solid var(--border); box-shadow: var(--shadow-sm);">
          <span class="w-2 h-2 rounded-full" style="background: var(--fern-300);"></span>
          <span style="font-size: 0.875rem; font-weight: 500; color: var(--fg-muted);">For volunteer drivers</span>
        </div>
      </div>

      <h1 style="font-family: var(--font-display); font-size: clamp(2.5rem, 6vw, 3.75rem); font-weight: 500; line-height: 1.1; letter-spacing: -0.02em; color: var(--fg); margin-bottom: 1.5rem;">
        Optimise your medicine<br />delivery route<br />
        <span style="color: var(--sky-600);">in seconds</span>
      </h1>

      <p style="font-family: var(--font-display); font-size: 1.5rem; font-weight: 400; line-height: 1.45; color: var(--fg-muted); max-width: 56ch; margin: 0 auto 3rem;">
        Help more patients faster. ScriptRunner finds the shortest route through your deliveries,
        considering real-time traffic and saving you time and fuel.
      </p>

      <div class="flex flex-col sm:flex-row gap-6 justify-center items-center">
        <Button
          variant="primary"
          size="lg"
          on:click={onStart}
        >
          <span class="flex items-center gap-2">
            <span>Start planning route</span>
            <ArrowRight size={20} />
          </span>
        </Button>

        <p style="font-size: 0.875rem; color: var(--fg-subtle);">
          No account needed · Free to use · UK postcodes only
        </p>
      </div>
    </div>

    <!-- Features Grid -->
    <div class="grid sm:grid-cols-2 gap-6 mb-16">
      {#each features as feature}
        <Card padding="md">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0" style="color: var(--teal-500);">
              <svelte:component this={feature.icon} size={28} strokeWidth={1.5} />
            </div>
            <div>
              <h3 style="font-family: var(--font-body); font-weight: 600; font-size: 1.25rem; color: var(--fg); margin-bottom: 0.5rem;">
                {feature.title}
              </h3>
              <p style="color: var(--fg-muted); font-size: 1rem; line-height: 1.55;">
                {feature.description}
              </p>
            </div>
          </div>
        </Card>
      {/each}
    </div>

    <!-- How it works -->
    <div class="text-center">
      <Card padding="lg" elevated={true}>
        <h2 style="font-family: var(--font-display); font-size: 2.25rem; font-weight: 500; line-height: 1.25; color: var(--fg); margin-bottom: 3rem;">
          How it works
        </h2>

        <div class="grid sm:grid-cols-3 gap-10 text-left">
          <div>
            <div class="flex items-center justify-center w-14 h-14 rounded-full font-bold mb-4" style="background: var(--accent); color: var(--paper-0); box-shadow: var(--shadow-sm); font-size: 1.5rem;">
              1
            </div>
            <h3 style="font-family: var(--font-body); font-weight: 600; font-size: 1.125rem; color: var(--fg); margin-bottom: 0.5rem;">
              Enter postcodes
            </h3>
            <p style="font-size: 0.875rem; color: var(--fg-muted); line-height: 1.55;">
              Paste your list of delivery addresses or add them one by one
            </p>
          </div>

          <div>
            <div class="flex items-center justify-center w-14 h-14 rounded-full font-bold mb-4" style="background: var(--accent); color: var(--paper-0); box-shadow: var(--shadow-sm); font-size: 1.5rem;">
              2
            </div>
            <h3 style="font-family: var(--font-body); font-weight: 600; font-size: 1.125rem; color: var(--fg); margin-bottom: 0.5rem;">
              Optimise route
            </h3>
            <p style="font-size: 0.875rem; color: var(--fg-muted); line-height: 1.55;">
              We calculate the shortest path considering current traffic
            </p>
          </div>

          <div>
            <div class="flex items-center justify-center w-14 h-14 rounded-full font-bold mb-4" style="background: var(--accent); color: var(--paper-0); box-shadow: var(--shadow-sm); font-size: 1.5rem;">
              3
            </div>
            <h3 style="font-family: var(--font-body); font-weight: 600; font-size: 1.125rem; color: var(--fg); margin-bottom: 0.5rem;">
              Navigate & track
            </h3>
            <p style="font-size: 0.875rem; color: var(--fg-muted); line-height: 1.55;">
              Follow the route with Google Maps and mark deliveries complete
            </p>
          </div>
        </div>
      </Card>
    </div>

    <!-- Privacy note -->
    <div class="mt-12 text-center flex items-center justify-center gap-2" style="font-size: 0.875rem; color: var(--fg-subtle);">
      <Lock size={16} />
      <span>Your privacy matters: We only use postcodes. No patient data. No tracking. No account required.</span>
    </div>
  </div>

  <!-- Footer -->
  <footer class="relative overflow-hidden mt-32" style="background: var(--sky-900); color: var(--paper-50); padding: 4rem 0 2rem;">
    <!-- Footer koru watermark (optimized) -->
    <img
      src="/mearsit-koru-bg.png"
      alt=""
      class="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] opacity-[0.02] pointer-events-none"
      aria-hidden="true"
      loading="lazy"
    />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
      <img
        src="/assets/mearsit-lockup-optimized.png"
        alt="mearsit"
        class="h-12 mx-auto mb-4 brightness-0 invert opacity-90"
        loading="lazy"
      />
      <p style="color: var(--sky-100); font-size: 0.9375rem; line-height: 1.6; max-width: 40ch; margin: 0 auto 2rem;">
        Route optimisation for medical delivery volunteers. Built with care by mearsit.
      </p>
      <p style="font-size: 0.875rem; color: var(--sky-100); padding-top: 2rem; border-top: 1px solid rgba(255, 255, 255, 0.1);">
        &copy; 2026 mearsit. A global software development company.
      </p>
    </div>
  </footer>
</div>
